import { PickpointStationShort } from './shipping-config.interface';

/**
 * Validierung des PickPoint-Abholtermins gegen Aktivierungszeitraum und
 * Wochentags-Öffnungszeiten der gewählten Station — ohne Angular-Abhängigkeiten,
 * damit das alte Formular und der geführte Versandziel-Dialog dieselbe Prüfung
 * nutzen.
 */

/**
 * Zerlegt einen datetime-local-Wert ("YYYY-MM-DDTHH:mm") in Datum
 * (UTC-Mitternacht), Wochentag (0=Mo..6=So) und "HH:mm". Liefert null bei
 * leerem/ungültigem Input. UTC-Mitternacht macht den Datums-Vergleich gegen
 * activation_date / cancellation_date zeitzonen-stabil.
 */
export function parsePickupDateTime(raw: string): { date: Date; day: number; hhmm: string } | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(raw);
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const day = (date.getUTCDay() + 6) % 7;
  return { date, day, hhmm: `${m[4]}:${m[5]}` };
}

export function parseIsoDateUtc(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

export function formatDateDe(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

/**
 * Fehlermeldung, wenn der Abholtermin außerhalb des Aktivierungszeitraums oder
 * der Öffnungszeiten der Station liegt — sonst null.
 */
export function pickupValidityError(
  station: PickpointStationShort | null | undefined,
  pickupDate: string,
): string | null {
  const parsed = parsePickupDateTime(pickupDate);
  if (!station || !parsed) return null;

  if (station.activation_date) {
    const act = parseIsoDateUtc(station.activation_date);
    if (act && parsed.date < act) return `Station erst ab ${formatDateDe(act)} aktiv.`;
  }
  if (station.cancellation_date) {
    const cancel = parseIsoDateUtc(station.cancellation_date);
    if (cancel && parsed.date >= cancel) return `Station ab ${formatDateDe(cancel)} nicht mehr aktiv.`;
  }

  const openings = station.openings ?? [];
  if (openings.length > 0) {
    const op = openings.find((o) => o.day === parsed.day) ?? null;
    if (!op) return 'An diesem Wochentag keine Öffnungszeiten hinterlegt.';
    if (parsed.hhmm < op.open || parsed.hhmm > op.close) {
      return `Uhrzeit liegt außerhalb der Öffnungszeiten (${op.open}–${op.close}).`;
    }
  }
  return null;
}

/** Anzeigetext für die Öffnungszeiten am gewählten Tag — oder null. */
export function pickupOpeningHint(
  station: PickpointStationShort | null | undefined,
  pickupDate: string,
): string | null {
  const parsed = parsePickupDateTime(pickupDate);
  if (!station?.openings?.length || !parsed) return null;
  const op = station.openings.find((o) => o.day === parsed.day);
  return op ? `Geöffnet ${op.open}–${op.close}` : null;
}
